import { fetchAllRows } from './fetchAllRows';

/**
 * Read EVERY row matching `.in(column, ids)` for an id list of any size.
 *
 * WHY: PostgREST takes `.in()` values in the GET query string, so a few
 * hundred UUIDs blow past the URL length limit and the request fails (or is
 * cut by a proxy). And every response is capped at 1000 rows with no error
 * (see fetchAllRows.js). This helper fixes both: ids are de-duplicated and
 * split into chunks, and each chunk is read through `fetchAllRows` so a chunk
 * that matches more than 1000 rows is paged too.
 *
 * CONTRACT for `buildQuery(chunkIds, selectOpts)`:
 *   - returns a FRESH builder on every call;
 *   - applies `.select(cols, selectOpts)` and `.in(column, chunkIds)` plus
 *     every other filter (tenant scope included);
 *   - applies a DETERMINISTIC order ending in a unique column (`.order('id')`).
 *
 * Results are concatenated in chunk order (rows of one id stay together and
 * keep the builder's order) and de-duplicated by `id` when rows carry one.
 * ANY chunk error fails the whole read: `{ data: null, error }` — never
 * partial data. Empty ids → `{ data: [], error: null }` with no query.
 *
 * @param {Array<string>} ids
 * @param {(chunkIds: string[], selectOpts: object) => object} buildQuery
 * @param {{ chunkSize?: number, concurrency?: number }} [opts]
 * @returns {Promise<{ data: Array|null, error: Error|null, count: number|null }>}
 */
export async function fetchAllRowsInChunks(ids, buildQuery, { chunkSize = 100, concurrency = 4 } = {}) {
  const unique = [...new Set((Array.isArray(ids) ? ids : []).filter((id) => id != null && id !== '').map(String))];
  if (unique.length === 0) return { data: [], error: null, count: 0 };

  const chunks = [];
  for (let i = 0; i < unique.length; i += chunkSize) chunks.push(unique.slice(i, i + chunkSize));

  const results = [];
  for (let i = 0; i < chunks.length; i += concurrency) {
    const batch = chunks.slice(i, i + concurrency);
    // eslint-disable-next-line no-await-in-loop
    const settled = await Promise.all(batch.map((chunk) => fetchAllRows((opts) => buildQuery(chunk, opts))));
    const failed = settled.find((r) => r.error);
    if (failed) return { data: null, error: failed.error, count: null };
    results.push(...settled);
  }

  const seen = new Set();
  const data = [];
  for (const r of results) {
    for (const row of r.data || []) {
      const id = row?.id;
      if (id != null) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      data.push(row);
    }
  }
  return { data, error: null, count: data.length };
}
